import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IconWrapper } from './IconWrapper';
import { 
  HiCheckCircle,
  HiExclamationCircle,
  HiInformationCircle,
  HiXMark
} from 'react-icons/hi2';

export type ToastType = 'success' | 'error' | 'info';

interface ToastProps {
  type: ToastType;
  message: string;
  isVisible: boolean;
  onClose: () => void;
}

const toastConfig = {
  success: {
    icon: HiCheckCircle,
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    borderColor: 'border-green-200 dark:border-green-800',
    textColor: 'text-green-800 dark:text-green-200'
  },
  error: {
    icon: HiExclamationCircle,
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-800',
    textColor: 'text-red-800 dark:text-red-200'
  },
  info: {
    icon: HiInformationCircle,
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-200 dark:border-blue-800',
    textColor: 'text-blue-800 dark:text-blue-200'
  }
};

export const Toast: React.FC<ToastProps> = ({ type, message, isVisible, onClose }) => {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(onClose, 5000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  const config = toastConfig[type];

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-4 right-4 z-50"
        >
          <div
            className={`flex items-center p-4 rounded-xl shadow-lg border ${config.bgColor} ${config.borderColor}`}
          >
            <IconWrapper icon={config.icon} className={`w-6 h-6 ${config.textColor}`} />
            <p className={`mx-3 font-medium ${config.textColor}`}>{message}</p>
            <button
              onClick={onClose}
              className={`p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 ${config.textColor}`}
            >
              <IconWrapper icon={HiXMark} className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};